import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	LinearProgress,
	IconButton,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import PATAddButton from "../../components/Buttons/PATAddButton";
import TextFieldController from "../../components/inputs/TextFieldController";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";

const equipmentSchema = yup.object().shape({
	// General
	status: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	type: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	categoryCode: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	propertyNumber: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.nullable(),
	assetTag: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	condition: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	serialNumber: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.nullable(),
	brand: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	model: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	style: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	color: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),

	// Acquisition
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	acquiredDate: isValidDateTest("Acquired Date"),
	replaceDate: yup.string().nullable(),
	purchasedFrom: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.nullable(),
	fundingSourceID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	lastInspectionDate: yup.string().nullable(),
	auditedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),

	// Assignment
	division: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	location: yup.string().trim().max(200, maxLengthMessage(200)).nullable(),
	assignedVehicleID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	assignedOfficerID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	assignedDate: yup.string().nullable(),
	assignedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),

	// Details
	caliber: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	sights: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	warrantyInfo: yup
		.string()
		.trim()
		.max(500, maxLengthMessage(500))
		.nullable(),
	comments: yup.string().trim().max(1000, maxLengthMessage(1000)).nullable(),

	serviceRequirements: yup.array().of(yup.number()).default([]),
});

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const buildDefaults = (initialData) => {
	return {
		// General
		status: initialData?.status || "",
		type: initialData?.type || "",
		categoryCode: initialData?.categoryCode || "",
		propertyNumber: initialData?.propertyNumber || "",
		assetTag: initialData?.assetTag || "",
		condition: initialData?.condition || "",
		serialNumber: initialData?.serialNumber || "",
		brand: initialData?.brand || "",
		model: initialData?.model || "",
		style: initialData?.style || "",
		color: initialData?.color || "",
		description: initialData?.description || "",

		// Acquisition
		cost: initialData?.cost || "",
		acquiredDate: initialData?.acquiredDate
			? moment(initialData.acquiredDate).format("MM/DD/YYYY")
			: "",
		replaceDate: initialData?.replaceDate || "",
		purchasedFrom: initialData?.purchasedFrom || "",
		fundingSourceID: initialData?.fundingSourceID
			? String(initialData.fundingSourceID)
			: "",
		lastInspectionDate: initialData?.lastInspectionDate || "",
		auditedBy: initialData?.auditedBy || "",

		// Assignment
		division: initialData?.division || "",
		location: initialData?.location || "",
		assignedVehicleID: initialData?.assignedVehicleID
			? String(initialData.assignedVehicleID)
			: "",
		assignedOfficerID: initialData?.assignedOfficerID
			? String(initialData.assignedOfficerID)
			: "",
		assignedDate: initialData?.assignedDate || "",
		assignedBy: initialData?.assignedBy || "",

		// Details
		caliber: initialData?.caliber || "",
		sights: initialData?.sights || "",
		warrantyInfo: initialData?.warrantyInfo || "",
		comments: initialData?.comments || "",

		serviceRequirements: initialData?.serviceRequirements || [],
	};
};

const AddEditEquipmentDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	// Options
	statusOptions,
	typeOptions,
	categoryCodeOptions,
	conditionOptions,
	divisionOptions,
	employeeOptions,
	vehicleOptions,
	fundingSourceOptions,
	serviceRequirements,
	// Assignment history
	assignmentHistory = [],
	// Maintenance data
	maintenanceRecords = [],
	onAddMaintenance,
	onEditMaintenance,
	canManageMaintenance = true,
}) => {
	const user = useSelector((state) => state.user);
	const [tab, setTab] = useState(0);
	const [showMaintModal, setShowMaintModal] = useState(false);
	const [editMaintRec, setEditMaintRec] = useState(null);
	const [maintForm, setMaintForm] = useState({
		date: "",
		performedBy: "",
		cost: "",
		description: "",
		status: "Completed",
		notes: "",
		svcReqID: "",
		fundingSourceID: "",
	});

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(equipmentSchema),
		defaultValues: buildDefaults(initialData),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData));
		setTab(0);
	}, [initialData, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
			setTab(0);
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			// General
			status: data.status || null,
			type: data.type || null,
			categoryCode: data.categoryCode || null,
			propertyNumber: data.propertyNumber?.trim() || null,
			assetTag: data.assetTag?.trim() || null,
			condition: data.condition || null,
			serialNumber: data.serialNumber?.trim() || null,
			brand: data.brand?.trim() || null,
			model: data.model?.trim() || null,
			style: data.style?.trim() || null,
			color: data.color?.trim() || null,
			description: data.description?.trim() || null,

			// Acquisition
			cost: data.cost || null,
			acquiredDate: data.acquiredDate
				? moment(data.acquiredDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			replaceDate: data.replaceDate || null,
			purchasedFrom: data.purchasedFrom?.trim() || null,
			fundingSourceID: data.fundingSourceID || null,
			lastInspectionDate: data.lastInspectionDate || null,
			auditedBy: data.auditedBy?.trim() || null,

			// Assignment
			division: data.division || null,
			location: data.location?.trim() || null,
			assignedVehicleID: data.assignedVehicleID || null,
			assignedOfficerID: data.assignedOfficerID || null,
			assignedDate: data.assignedDate || null,
			assignedBy: data.assignedBy?.trim() || null,

			// Details
			caliber: data.caliber?.trim() || null,
			sights: data.sights?.trim() || null,
			warrantyInfo: data.warrantyInfo?.trim() || null,
			comments: data.comments?.trim() || null,

			serviceRequirements: data.serviceRequirements || [],

			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	// Service requirements
	const assignedReqs = watch("serviceRequirements") || [];
	const activeReqs = (serviceRequirements || []).filter(
		(r) => r.isActive !== false,
	);

	const toggleReq = (id) => {
		const next = assignedReqs.includes(id)
			? assignedReqs.filter((x) => x !== id)
			: [...assignedReqs, id];
		setValue("serviceRequirements", next, { shouldDirty: true });
	};

	// Assignment history
	const eqHistory = initialData?.id
		? [
				...assignmentHistory.filter(
					(h) => h.equipmentID === initialData.id,
				),
			].sort(
				(a, b) => new Date(b.assignedDate) - new Date(a.assignedDate),
			)
		: [];

	// Maintenance records for this equipment
	const eqMaintenance = initialData?.id
		? maintenanceRecords.filter((m) => m.equipmentID === initialData.id)
		: [];

	// Open add/edit maintenance modal
	const openAddMaint = () => {
		setEditMaintRec(null);
		setMaintForm({
			date: moment().format("YYYY-MM-DD"),
			performedBy: "",
			cost: "",
			description: "",
			status: "Completed",
			notes: "",
			svcReqID: "",
			fundingSourceID: "",
		});
		setShowMaintModal(true);
	};

	const openEditMaint = (record) => {
		setEditMaintRec(record);
		setMaintForm({
			date: record.date || "",
			performedBy: record.performedBy || "",
			cost: record.cost ?? "",
			description: record.description || "",
			status: record.status || "Completed",
			notes: record.notes || "",
			svcReqID: record.svcReqID || "",
			fundingSourceID: record.fundingSourceID
				? String(record.fundingSourceID)
				: "",
		});
		setShowMaintModal(true);
	};

	const saveMaintRecord = () => {
		const record = {
			...maintForm,
			equipmentID: initialData.id,
			cost: maintForm.cost !== "" ? +maintForm.cost : null,
			svcReqID: maintForm.svcReqID || null,
			fundingSourceID: maintForm.fundingSourceID
				? +maintForm.fundingSourceID
				: null,
		};

		if (editMaintRec) {
			onEditMaintenance({ ...editMaintRec, ...record });
		} else {
			onAddMaintenance({ ...record, id: Date.now() });
		}
		setShowMaintModal(false);
		setEditMaintRec(null);
	};

	// Service requirement status calculation
	const getReqStatus = (reqId) => {
		const sr = activeReqs.find((r) => r.id === reqId);
		if (!sr) return null;

		const reqHistory = eqMaintenance.filter(
			(m) => m.svcReqID === reqId && m.status === "Completed",
		);
		const last = [...reqHistory].sort(
			(a, b) => new Date(b.date) - new Date(a.date),
		)[0];
		const lastDate = last?.date || initialData?.acquiredDate || null;

		if (!lastDate) {
			return { remaining: 0, overdue: true, pct: 100, lastDate: null };
		}

		const daysSince = moment().diff(moment(lastDate), "days");
		const remaining = sr.intervalDays - daysSince;
		const pct = Math.max(
			0,
			Math.min(100, (daysSince / sr.intervalDays) * 100),
		);
		const overdue = remaining <= 0;
		const dueSoon = !overdue && remaining <= sr.intervalDays * 0.1;

		return { remaining, overdue, dueSoon, pct, lastDate, daysSince };
	};

	// Maintenance chart calculation
	const getMaintChartData = () => {
		const chartRecs = eqMaintenance.filter((m) => m.cost > 0 && m.date);
		const totalCost = chartRecs.reduce((s, m) => s + (m.cost || 0), 0);

		let startDate = initialData?.acquiredDate
			? moment(initialData.acquiredDate).startOf("quarter")
			: moment().subtract(11, "months").startOf("month");

		const endDate = moment();
		const buckets = [];

		let cur = moment(startDate);
		while (cur.isBefore(endDate) || cur.isSame(endDate, "quarter")) {
			const qEnd = moment(cur).add(3, "months");
			const label = `Q${Math.floor(cur.month() / 3) + 1} '${String(cur.year()).slice(2)}`;
			const cost = chartRecs
				.filter((m) => {
					const d = moment(m.date);
					return (
						d.isSameOrAfter(cur, "day") && d.isBefore(qEnd, "day")
					);
				})
				.reduce((s, m) => s + (m.cost || 0), 0);

			buckets.push({ label, cost });
			cur.add(3, "months");

			if (buckets.length > 12) break;
		}

		return { buckets, totalCost };
	};

	const dialogTitle = initialData
		? viewOnly
			? `View Equipment — ${initialData.serialNumber}`
			: `Edit Equipment — ${initialData.serialNumber}`
		: "Add Equipment";

	const fmtDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : "—");
	const fmtCost = (n) =>
		n
			? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: "—";

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>{dialogTitle}</DialogTitle>

				<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
					<Tabs
						value={tab}
						onChange={(_, v) => setTab(v)}
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab label="General" />
						<Tab label="Acquisition" />
						<Tab label="Assignment" />
						<Tab label="Details" />
						<Tab label="Service Req." />
						<Tab label="Maintenance" />
					</Tabs>
				</Box>

				<DialogContent
					sx={{
						pointerEvents: viewOnly ? "none" : undefined,
						opacity: viewOnly ? 0.75 : undefined,
					}}
				>
					<Box
						component="form"
						onSubmit={handleSubmit(onSubmit)}
						noValidate
					>
						{/* ── GENERAL ── */}
						<TabPanel value={tab} index={0}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(3, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<DropdownController
										name="status"
										label="Status"
										options={statusOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DropdownController
										name="type"
										label="Type"
										options={typeOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DropdownController
										name="categoryCode"
										label="Category Code"
										options={categoryCodeOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="propertyNumber"
										label="Property Number"
										control={control}
										errors={errors}
										placeholder="PROP-2024-0001"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="assetTag"
										label="Asset Tag"
										control={control}
										errors={errors}
										placeholder="e.g. AT-4421"
									/>
								</Box>
								<Box>
									<DropdownController
										name="condition"
										label="Condition"
										options={conditionOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="serialNumber"
										label="Serial Number"
										control={control}
										errors={errors}
										placeholder="e.g. SN-12345"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="brand"
										label="Brand / Make"
										control={control}
										errors={errors}
										placeholder="e.g. Motorola"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="model"
										label="Model"
										control={control}
										errors={errors}
										placeholder="e.g. APX 8000"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="style"
										label="Style"
										control={control}
										errors={errors}
										placeholder="e.g. Portable, In-Vehicle"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="color"
										label="Color"
										control={control}
										errors={errors}
										placeholder="e.g. Black"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="description"
										label="Description"
										control={control}
										errors={errors}
										placeholder="Brief description of item"
										multiline
										rows={2}
									/>
								</Box>
							</Box>
						</TabPanel>

						{/* ── ACQUISITION ── */}
						<TabPanel value={tab} index={1}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<TextFieldController
										name="cost"
										label="Cost ($)"
										control={control}
										errors={errors}
										type="number"
										placeholder="0.00"
									/>
								</Box>
								<Box>
									<DatePickerController
										name="acquiredDate"
										label="Acquired Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DatePickerController
										name="replaceDate"
										label="Replace Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="purchasedFrom"
										label="Purchased From"
										control={control}
										errors={errors}
										placeholder="Vendor / supplier name"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<DropdownController
										name="fundingSourceID"
										label="Funding Source"
										options={fundingSourceOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>

								{/* Audit Section */}
								<Box
									sx={{
										gridColumn: "1 / -1",
										pt: 2,
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
											}}
										>
											Audit
										</Typography>
										{!viewOnly && (
											<PATAddButton
												onClick={() => {
													setValue(
														"lastInspectionDate",
														moment().format(
															"YYYY-MM-DD",
														),
														{ shouldDirty: true },
													);
													setValue(
														"auditedBy",
														user?.name ||
															"Current User",
														{ shouldDirty: true },
													);
												}}
												tooltipText="Audit Item"
												size="small"
											/>
										)}
									</Box>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns:
												"repeat(2, 1fr)",
											gap: 2,
										}}
									>
										<Box>
											<DatePickerController
												name="lastInspectionDate"
												label="Last Audit Date"
												control={control}
												errors={errors}
											/>
										</Box>
										<Box>
											<TextFieldController
												name="auditedBy"
												label="Audited By"
												control={control}
												errors={errors}
												placeholder="Audited by"
											/>
										</Box>
									</Box>
								</Box>
							</Box>
						</TabPanel>

						{/* ── ASSIGNMENT ── */}
						<TabPanel value={tab} index={2}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<DropdownController
										name="division"
										label="Division"
										options={divisionOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="location"
										label="Location"
										control={control}
										errors={errors}
										placeholder="e.g. Vehicle P-101, Armory Rack 3"
									/>
								</Box>
								<Box>
									<DropdownController
										name="assignedVehicleID"
										label="Assigned Vehicle"
										options={vehicleOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>
								<Box>
									<DropdownController
										name="assignedOfficerID"
										label="Assigned Employee"
										options={employeeOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>
								<Box>
									<DatePickerController
										name="assignedDate"
										label="Assigned Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="assignedBy"
										label="Assigned By"
										control={control}
										errors={errors}
										placeholder="Name / role of assigning employee"
									/>
								</Box>
							</Box>

							{/* Assignment History */}
							{initialData?.id && (
								<Box sx={{ mt: 3 }}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1,
											display: "block",
										}}
									>
										Assignment History
										<Typography
											component="span"
											sx={{
												fontWeight: 400,
												textTransform: "none",
												ml: 1,
												color: "text.disabled",
											}}
										>
											{eqHistory.length} record
											{eqHistory.length !== 1 ? "s" : ""}
										</Typography>
									</Typography>

									{eqHistory.length === 0 ? (
										<Box
											sx={{
												p: 2,
												textAlign: "center",
												color: "text.disabled",
												fontSize: 12,
												bgcolor: "background.default",
												borderRadius: 1,
												border: 1,
												borderColor: "divider",
											}}
										>
											No assignment history yet
										</Box>
									) : (
										<TableContainer
											sx={{
												maxHeight: 220,
												border: 1,
												borderColor: "divider",
												borderRadius: 1,
											}}
										>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Employee
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Vehicle
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Location
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Assigned
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Unassigned
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															Days
														</TableCell>
														<TableCell
															sx={{
																fontSize: 10,
																fontWeight: 700,
																textTransform:
																	"uppercase",
															}}
														>
															By
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{eqHistory.map((h) => {
														const emp =
															employeeOptions?.find(
																(e) =>
																	String(
																		e.value,
																	) ===
																	String(
																		h.officerID,
																	),
															);
														const veh =
															vehicleOptions?.find(
																(v) =>
																	String(
																		v.value,
																	) ===
																	String(
																		h.vehicleID,
																	),
															);
														const isCurrent =
															!h.unassignedDate;
														const dur = Math.round(
															(new Date(
																h.unassignedDate ||
																	new Date(),
															) -
																new Date(
																	h.assignedDate,
																)) /
																86400000,
														);

														return (
															<TableRow
																key={h.id}
																hover
															>
																<TableCell
																	sx={{
																		fontWeight: 600,
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	{isCurrent && (
																		<Box
																			sx={{
																				width: 6,
																				height: 6,
																				borderRadius:
																					"50%",
																				bgcolor:
																					"success.main",
																			}}
																		/>
																	)}
																	{emp?.label ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																		fontSize: 11,
																		color: "text.secondary",
																	}}
																>
																	{veh?.label ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		color: "text.secondary",
																	}}
																>
																	{h.location ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																		fontSize: 11,
																	}}
																>
																	{fmtDate(
																		h.assignedDate,
																	)}
																</TableCell>
																<TableCell>
																	{isCurrent ? (
																		<Chip
																			label="Current"
																			size="small"
																			color="success"
																			sx={{
																				fontSize: 10,
																				height: 20,
																			}}
																		/>
																	) : (
																		<Box
																			sx={{
																				fontFamily:
																					"monospace",
																				fontSize: 11,
																			}}
																		>
																			{fmtDate(
																				h.unassignedDate,
																			)}
																		</Box>
																	)}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																	}}
																>
																	{dur}d
																</TableCell>
																<TableCell
																	sx={{
																		color: "text.secondary",
																	}}
																>
																	{h.assignedBy ||
																		"—"}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</Box>
							)}
						</TabPanel>

						{/* ── DETAILS ── */}
						<TabPanel value={tab} index={3}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<TextFieldController
										name="caliber"
										label="Caliber"
										control={control}
										errors={errors}
										placeholder="e.g. 9mm, 5.56mm NATO, .40 S&W"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="sights"
										label="Sights"
										control={control}
										errors={errors}
										placeholder="e.g. Iron Sights, Aimpoint PRO"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="warrantyInfo"
										label="Warranty Information"
										control={control}
										errors={errors}
										placeholder="Warranty terms, expiration, contact..."
										multiline
										rows={3}
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="comments"
										label="Comments"
										control={control}
										errors={errors}
										placeholder="Additional notes or observations..."
										multiline
										rows={3}
									/>
								</Box>
							</Box>
						</TabPanel>

						{/* ── SERVICE REQUIREMENTS ── */}
						<TabPanel value={tab} index={4}>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.secondary"
								sx={{
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									mb: 2,
									display: "block",
								}}
							>
								Assign Service Requirements
								<Typography
									component="span"
									sx={{
										fontWeight: 400,
										textTransform: "none",
										ml: 1,
										color: "text.disabled",
										fontSize: 11,
									}}
								>
									Day-based intervals tracked against
									maintenance history
								</Typography>
							</Typography>

							{activeReqs.length === 0 ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									No service requirements defined. Add them in
									Code Maintenance → Equipment Service
									Requirements.
								</Box>
							) : (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 1.5,
										mb: 3,
									}}
								>
									{activeReqs.map((sr) => {
										const checked = assignedReqs.includes(
											sr.id,
										);
										return (
											<Box
												key={sr.id}
												onClick={() =>
													!viewOnly &&
													toggleReq(sr.id)
												}
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1.5,
													p: "10px 14px",
													bgcolor: checked
														? "rgba(63, 131, 245, 0.08)"
														: "background.paper",
													border: "1px solid",
													borderColor: checked
														? "primary.main"
														: "divider",
													borderRadius: 1,
													cursor: viewOnly
														? "default"
														: "pointer",
													transition: "all 0.12s",
													"&:hover": viewOnly
														? {}
														: {
																borderColor:
																	"primary.main",
															},
												}}
											>
												<input
													type="checkbox"
													checked={checked}
													onChange={() =>
														!viewOnly &&
														toggleReq(sr.id)
													}
													style={{
														width: 18,
														height: 18,
														cursor: viewOnly
															? "default"
															: "pointer",
													}}
												/>
												<Box sx={{ flex: 1 }}>
													<Typography
														variant="body2"
														fontWeight={700}
													>
														{sr.label}
													</Typography>
													<Typography
														variant="caption"
														color="text.secondary"
													>
														Every {sr.intervalDays}{" "}
														day
														{sr.intervalDays !== 1
															? "s"
															: ""}
														{sr.intervalDays === 365
															? " (Annual)"
															: sr.intervalDays ===
																  180
																? " (Semi-annual)"
																: sr.intervalDays ===
																	  90
																	? " (Quarterly)"
																	: sr.intervalDays ===
																		  30
																		? " (Monthly)"
																		: ""}
													</Typography>
												</Box>
												{checked && (
													<Chip
														label="Active"
														size="small"
														color="primary"
														sx={{
															fontSize: 10,
															height: 20,
														}}
													/>
												)}
											</Box>
										);
									})}
								</Box>
							)}

							{/* Service Requirement Status */}
							{initialData?.id && assignedReqs.length > 0 && (
								<Box>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1.5,
											display: "block",
										}}
									>
										Current Status
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
										}}
									>
										{assignedReqs.map((id) => {
											const sr = activeReqs.find(
												(r) => r.id === id,
											);
											if (!sr) return null;

											const status = getReqStatus(id);
											const color = status.overdue
												? "error.main"
												: status.dueSoon
													? "warning.main"
													: "success.main";
											const bgColor = status.overdue
												? "error.light"
												: status.dueSoon
													? "warning.light"
													: "success.light";

											return (
												<Box
													key={id}
													sx={{
														p: 1.5,
														bgcolor:
															"background.paper",
														border: 1,
														borderColor: "divider",
														borderRadius: 1,
													}}
												>
													<Box
														sx={{
															display: "flex",
															justifyContent:
																"space-between",
															alignItems:
																"center",
															mb: 0.5,
														}}
													>
														<Typography
															variant="body2"
															fontWeight={600}
														>
															{sr.label}
														</Typography>
														<Typography
															variant="caption"
															fontWeight={700}
															color={color}
														>
															{status.overdue
																? `${Math.abs(status.remaining)}d overdue`
																: `${status.remaining}d left`}
														</Typography>
													</Box>
													<LinearProgress
														variant="determinate"
														value={status.pct}
														sx={{
															height: 4,
															borderRadius: 1,
															bgcolor: "grey.200",
															"& .MuiLinearProgress-bar":
																{
																	bgcolor:
																		color,
																},
														}}
													/>
													<Typography
														variant="caption"
														color="text.disabled"
														sx={{
															mt: 0.5,
															display: "block",
														}}
													>
														{status.lastDate
															? `Last: ${fmtDate(status.lastDate)} (${status.daysSince}d ago)`
															: "Never serviced"}
													</Typography>
												</Box>
											);
										})}
									</Box>
								</Box>
							)}
						</TabPanel>

						{/* ── MAINTENANCE ── */}
						<TabPanel value={tab} index={5}>
							{/* Maintenance Cost Chart */}
							{initialData?.id && (
								<Box
									sx={{
										mb: 2,
										pb: 2,
										borderBottom: 1,
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											🔩 Maintenance Costs
											<Typography
												component="span"
												sx={{
													fontFamily: "monospace",
													fontWeight: 400,
													textTransform: "none",
													color: "text.disabled",
													fontSize: 11,
												}}
											>
												{fmtCost(
													getMaintChartData()
														.totalCost,
												)}{" "}
												total · {eqMaintenance.length}{" "}
												record
												{eqMaintenance.length !== 1
													? "s"
													: ""}
											</Typography>
										</Typography>
									</Box>

									{eqMaintenance.filter((m) => m.cost > 0)
										.length > 0 ? (
										<Box
											sx={{
												display: "flex",
												gap: 0.5,
												alignItems: "flex-end",
												height: 80,
											}}
										>
											{getMaintChartData().buckets.map(
												(bucket, i) => {
													const maxCost = Math.max(
														...getMaintChartData().buckets.map(
															(b) => b.cost,
														),
														1,
													);
													const height =
														bucket.cost > 0
															? Math.max(
																	4,
																	(bucket.cost /
																		maxCost) *
																		72,
																)
															: 4;
													return (
														<Box
															key={i}
															sx={{
																flex: 1,
																display: "flex",
																flexDirection:
																	"column",
																alignItems:
																	"center",
																gap: 0.5,
															}}
															title={`${bucket.label}: ${fmtCost(bucket.cost)}`}
														>
															<Box
																sx={{
																	width: "100%",
																	height,
																	bgcolor:
																		bucket.cost >
																		0
																			? "primary.main"
																			: "grey.300",
																	borderRadius: 0.5,
																	opacity:
																		bucket.cost >
																		0
																			? 0.8
																			: 0.4,
																	transition:
																		"height 0.3s",
																}}
															/>
															<Typography
																variant="caption"
																sx={{
																	fontSize: 8,
																	color: "text.disabled",
																}}
															>
																{bucket.label}
															</Typography>
														</Box>
													);
												},
											)}
										</Box>
									) : (
										<Box
											sx={{
												height: 80,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												color: "text.disabled",
												fontSize: 12,
											}}
										>
											No maintenance costs recorded
										</Box>
									)}
								</Box>
							)}

							{/* Add Maintenance Button */}
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 2,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Maintenance History
									{eqMaintenance.length > 0 && (
										<Typography
											component="span"
											sx={{
												fontWeight: 400,
												color: "text.disabled",
												ml: 1,
											}}
										>
											({eqMaintenance.length})
										</Typography>
									)}
								</Typography>
								{initialData?.id &&
									canManageMaintenance &&
									!viewOnly && (
										<PATAddButton
											onClick={openAddMaint}
											tooltipText="Add Maintenance Record"
											size="small"
										/>
									)}
							</Box>

							{!initialData?.id ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									Save the equipment record first to log
									maintenance
								</Box>
							) : eqMaintenance.length === 0 ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									No maintenance records yet
								</Box>
							) : (
								<TableContainer
									sx={{
										maxHeight: 280,
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Date
												</TableCell>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Description
												</TableCell>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Performed By
												</TableCell>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Cost
												</TableCell>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Req
												</TableCell>
												<TableCell
													sx={{
														fontSize: 10,
														fontWeight: 700,
														textTransform:
															"uppercase",
													}}
												>
													Status
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{eqMaintenance.map((m, i) => {
												const sr = activeReqs.find(
													(r) => r.id === m.svcReqID,
												);
												return (
													<TableRow
														key={m.id}
														hover
														onClick={() =>
															canManageMaintenance &&
															openEditMaint(m)
														}
														sx={{
															cursor: canManageMaintenance
																? "pointer"
																: "default",
															"&:nth-of-type(odd)":
																{
																	bgcolor:
																		"grey.50",
																},
														}}
													>
														<TableCell
															sx={{
																fontFamily:
																	"monospace",
																fontSize: 11,
																whiteSpace:
																	"nowrap",
															}}
														>
															{fmtDate(m.date)}
														</TableCell>
														<TableCell
															sx={{
																maxWidth: 180,
															}}
														>
															<Typography
																variant="body2"
																noWrap
															>
																{m.description ||
																	"—"}
															</Typography>
															{m.notes && (
																<Typography
																	variant="caption"
																	color="text.disabled"
																>
																	{m.notes}
																</Typography>
															)}
														</TableCell>
														<TableCell
															sx={{
																color: "text.secondary",
																whiteSpace:
																	"nowrap",
															}}
														>
															{m.performedBy ||
																"—"}
														</TableCell>
														<TableCell
															sx={{
																fontFamily:
																	"monospace",
																whiteSpace:
																	"nowrap",
															}}
														>
															{fmtCost(m.cost)}
														</TableCell>
														<TableCell>
															{sr ? (
																<Chip
																	label={
																		sr.label
																	}
																	size="small"
																	color="primary"
																	sx={{
																		fontSize: 10,
																		height: 20,
																	}}
																/>
															) : (
																<Typography
																	variant="body2"
																	color="text.disabled"
																>
																	—
																</Typography>
															)}
														</TableCell>
														<TableCell>
															<Chip
																label={m.status}
																size="small"
																color={
																	m.status ===
																	"Completed"
																		? "success"
																		: m.status ===
																			  "In Progress"
																			? "info"
																			: m.status ===
																				  "Pending"
																				? "warning"
																				: m.status ===
																					  "Cancelled"
																					? "default"
																					: "primary"
																}
																sx={{
																	fontSize: 10,
																	height: 20,
																}}
															/>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</TabPanel>
					</Box>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2 }}>
					{!viewOnly && (
						<PATSaveButton
							onClick={handleSubmit(onSubmit)}
							tooltipText="Save"
						/>
					)}
					<PATCloseButton onClick={onClose} tooltipText="Close" />
				</DialogActions>
			</Dialog>

			{/* Maintenance Record Dialog */}
			<Dialog
				open={showMaintModal}
				onClose={() => setShowMaintModal(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{editMaintRec
						? `Edit Maintenance Record`
						: `New Maintenance Record — ${initialData?.type || ""} ${initialData?.serialNumber || ""}`}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 2,
							pt: 1,
						}}
					>
						<Box>
							<TextFieldController
								name="maintDate"
								label="Date"
								value={maintForm.date}
								onChange={(e) =>
									setMaintForm((p) => ({
										...p,
										date: e.target.value,
									}))
								}
								type="date"
							/>
						</Box>
						<Box>
							<DropdownController
								name="maintStatus"
								label="Status"
								value={maintForm.status}
								onChange={(val) =>
									setMaintForm((p) => ({ ...p, status: val }))
								}
								options={[
									"Scheduled",
									"Pending",
									"In Progress",
									"Completed",
									"Cancelled",
								].map((s) => ({ value: s, label: s }))}
							/>
						</Box>
						<Box>
							<TextFieldController
								name="maintPerformedBy"
								label="Performed By"
								value={maintForm.performedBy}
								onChange={(e) =>
									setMaintForm((p) => ({
										...p,
										performedBy: e.target.value,
									}))
								}
								placeholder="Name or organization"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="maintCost"
								label="Cost ($)"
								value={maintForm.cost}
								onChange={(e) =>
									setMaintForm((p) => ({
										...p,
										cost: e.target.value,
									}))
								}
								type="number"
								placeholder="0.00"
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="maintSvcReq"
								label="Fulfills Service Requirement (optional)"
								value={maintForm.svcReqID}
								onChange={(val) =>
									setMaintForm((p) => ({
										...p,
										svcReqID: val,
									}))
								}
								options={activeReqs
									.filter((sr) =>
										assignedReqs.includes(sr.id),
									)
									.map((sr) => ({
										value: sr.id,
										label: `${sr.label} (every ${sr.intervalDays}d)`,
									}))}
								placeholder="— Not linked to a service requirement —"
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="maintFunding"
								label="Funding Source (optional)"
								value={maintForm.fundingSourceID}
								onChange={(val) =>
									setMaintForm((p) => ({
										...p,
										fundingSourceID: val,
									}))
								}
								options={fundingSourceOptions || []}
								placeholder="— Not linked to funding source —"
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="maintDescription"
								label="Description"
								value={maintForm.description}
								onChange={(e) =>
									setMaintForm((p) => ({
										...p,
										description: e.target.value,
									}))
								}
								placeholder="Describe the maintenance performed..."
								multiline
								rows={2}
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="maintNotes"
								label="Notes"
								value={maintForm.notes}
								onChange={(e) =>
									setMaintForm((p) => ({
										...p,
										notes: e.target.value,
									}))
								}
								placeholder="Parts used, follow-up actions..."
								multiline
								rows={2}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					{editMaintRec && (
						<PATCloseButton
							onClick={() => setShowMaintModal(false)}
							tooltipText="Close"
						/>
					)}
					<PATCloseButton
						onClick={() => {
							setShowMaintModal(false);
							setEditMaintRec(null);
						}}
						tooltipText="Cancel"
					/>
					<PATSaveButton
						onClick={saveMaintRecord}
						tooltipText="Save Record"
					/>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddEditEquipmentDialog;
